import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, CardContent, Badge, PageHeader, EmptyState, Button, Input, Label } from '@/components/ui'
import { adminApi } from '@/lib/api'
import { Building2, ChevronRight, Package, ShoppingCart, Key, Plus, X, Copy, Check, AlertTriangle, Loader2 } from 'lucide-react'

export const Route = createFileRoute('/_app/tenants/')({
  component: TenantsListPage,
})

interface TenantRich {
  id: string
  slug: string
  name: string
  primaryHostname: string | null
  storefrontUrl: string | null
  status: 'active' | 'past_due' | 'suspended' | 'canceled'
  defaultCurrency: string
  defaultLocale: string
  metadata: Record<string, string>
  createdAt: string
  plan: 'starter' | 'growth' | 'enterprise'
  productsCount: number
  ordersCount: number
  apiKeysCount: number
  usageThisMonth: { apiRequests: number; ordersCreated: number }
}

function TenantsListPage() {
  const [showCreate, setShowCreate] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['tenants-rich'],
    queryFn: () => adminApi.get<{ data: TenantRich[] }>('/api/admin/tenants-rich'),
    refetchInterval: 30_000,
  })

  const tenants = data?.data ?? []

  return (
    <>
      <PageHeader
        title="Tiendas"
        description={`${tenants.length} tienda(s) en la plataforma`}
        action={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="size-4 mr-2" /> Crear tienda
          </Button>
        }
      />
      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} />}
      <div className="p-8 space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {!isLoading && tenants.length === 0 && (
          <EmptyState title="Sin tiendas" description="Crea la primera tienda con el botón arriba" />
        )}
        {tenants.map((tenant) => (
          <Link
            key={tenant.id}
            to="/tenants/$id"
            params={{ id: tenant.id }}
            className="block group"
          >
            <Card className="hover:border-border transition cursor-pointer">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded bg-[hsl(var(--muted))] flex items-center justify-center shrink-0">
                    <Building2 className="size-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{tenant.name}</h3>
                      <PlanBadge plan={tenant.plan} />
                      <StatusBadge status={tenant.status} />
                      {tenant.metadata.industry && (
                        <Badge variant="muted">{tenant.metadata.industry}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                      <span className="font-mono">{tenant.slug}</span>
                      {tenant.storefrontUrl && (
                        <span className="font-mono truncate max-w-xs">{tenant.storefrontUrl.replace(/^https?:\/\//, '')}</span>
                      )}
                      <span>{tenant.defaultCurrency} · {tenant.defaultLocale}</span>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <Stat icon={Package} label="productos" value={tenant.productsCount} />
                    <Stat icon={ShoppingCart} label="órdenes" value={tenant.ordersCount} />
                    <Stat icon={Key} label="API keys" value={tenant.apiKeysCount} />
                  </div>
                  <ChevronRight className="size-5 text-muted-foreground group-hover:text-muted-foreground transition" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </>
  )
}

function Stat({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number }) {
  return (
    <div className="text-center min-w-[60px]">
      <Icon className="size-4 mx-auto text-muted-foreground mb-1" />
      <p className="font-mono text-sm">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  )
}

function PlanBadge({ plan }: { plan: string }) {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'muted'> = {
    starter: 'muted',
    growth: 'default',
    enterprise: 'success',
  }
  return <Badge variant={variants[plan] ?? 'muted'}>{plan}</Badge>
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'muted'> = {
    active: 'success',
    past_due: 'warning',
    suspended: 'destructive',
    canceled: 'muted',
  }
  return <Badge variant={variants[status] ?? 'muted'}>{status}</Badge>
}

interface ProvisionResult {
  tenant: { id: string; name: string; slug: string }
  apiKey: { id: string; name: string; prefix: string; environment: string; fullKey: string }
  owner: { id: string; email: string } | null
  invitation: { email: string; setupUrl: string; expiresAt: string } | null
}

function CreateTenantModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [primaryHostname, setPrimaryHostname] = useState('')
  const [storefrontUrl, setStorefrontUrl] = useState('')
  const [defaultCurrency, setDefaultCurrency] = useState('COP')
  const [defaultLocale, setDefaultLocale] = useState('es')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [ownerPassword, setOwnerPassword] = useState('')
  const [industry, setIndustry] = useState('')
  const [environment, setEnvironment] = useState<'live' | 'test'>('live')
  const [result, setResult] = useState<ProvisionResult | null>(null)
  const [copied, setCopied] = useState(false)

  const provision = useMutation({
    mutationFn: (input: unknown) =>
      adminApi.post<{ data: ProvisionResult }>('/api/admin/tenants/provision', input),
    onSuccess: (res) => {
      setResult(res.data)
      void qc.invalidateQueries({ queryKey: ['tenants-rich'] })
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    provision.mutate({
      name,
      slug,
      primaryHostname: primaryHostname.trim() || undefined,
      storefrontUrl: storefrontUrl.trim() || undefined,
      defaultCurrency,
      defaultLocale,
      ownerEmail: ownerEmail.trim() || undefined,
      ownerName: ownerName.trim() || undefined,
      ownerPassword: ownerPassword || undefined,
      industry: industry.trim() || undefined,
      apiKeyEnvironment: environment,
      apiKeyName: 'Default storefront key',
    })
  }

  function copyKey() {
    if (!result) return
    void navigator.clipboard.writeText(result.apiKey.fullKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold">{result ? 'Tienda creada' : 'Nueva tienda'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="size-5" /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="p-4 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{result.tenant.name}</p>
              <p className="text-xs text-emerald-500/80 font-mono mt-0.5">slug: {result.tenant.slug}</p>
              {result.owner && (
                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-2">
                  ✓ Owner creado: <span className="font-mono">{result.owner.email}</span> — ya puede loguearse al admin con las credenciales que pasaste.
                </p>
              )}
              {result.invitation && (
                <div className="mt-3 p-3 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 space-y-2">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    📧 Invitación enviada a <span className="font-mono">{result.invitation.email}</span>. Vence el {new Date(result.invitation.expiresAt).toLocaleDateString('es-CO')}.
                  </p>
                  <div className="flex items-stretch gap-2">
                    <Input value={result.invitation.setupUrl} readOnly className="font-mono text-[10px]" />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (result.invitation) {
                          void navigator.clipboard.writeText(result.invitation.setupUrl)
                        }
                      }}
                    >
                      <Copy className="size-3.5" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-blue-700 dark:text-blue-300/70">
                    Si el email no llega, compartí este link manualmente.
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 rounded-md bg-yellow-50 dark:bg-yellow-900/15 border border-yellow-200 dark:border-yellow-800/60">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="size-4 text-yellow-700 dark:text-yellow-400 mt-0.5 shrink-0" />
                <p className="text-xs text-yellow-700 dark:text-yellow-300 leading-relaxed">
                  Esta es la <strong>única vez</strong> que vas a ver la API key completa. Copiala y guardala
                  en un sitio seguro — el storefront del tenant la necesita.
                </p>
              </div>
              <div className="flex items-stretch gap-2">
                <Input value={result.apiKey.fullKey} readOnly className="font-mono text-xs" />
                <Button type="button" variant="outline" onClick={copyKey}>
                  {copied ? <><Check className="size-4 mr-1" /> Copiado</> : <><Copy className="size-4 mr-1" /> Copiar</>}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 font-mono">
                {result.apiKey.environment} · {result.apiKey.prefix}
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
              <Link to="/tenants/$id" params={{ id: result.tenant.id }} onClick={onClose}>
                <Button>Ver tienda <ChevronRight className="size-4 ml-1" /></Button>
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div><Label>Nombre *</Label><Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="ACME Streetwear" /></div>
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                required
                placeholder="acme-streetwear"
              />
              <p className="text-[10px] text-muted-foreground mt-1">solo minúsculas, números y guiones</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Currency</Label><Input value={defaultCurrency} onChange={(e) => setDefaultCurrency(e.target.value.toUpperCase())} maxLength={3} /></div>
              <div><Label>Locale</Label><Input value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)} maxLength={5} /></div>
            </div>

            <div><Label>Hostname principal (opcional)</Label><Input value={primaryHostname} onChange={(e) => setPrimaryHostname(e.target.value)} placeholder="store.acme.com" /></div>
            <div><Label>Storefront URL (opcional)</Label><Input value={storefrontUrl} onChange={(e) => setStorefrontUrl(e.target.value)} placeholder="https://store.acme.com" /></div>

            <div className="border-t border-border pt-4 space-y-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Owner (opcional)</p>
              <p className="text-[11px] text-muted-foreground -mt-3 leading-relaxed">
                Si pasás email + password, se crea un usuario que puede loguearse al admin con esas credenciales (rol owner del tenant).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@acme.com" /></div>
                <div><Label>Nombre</Label><Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} /></div>
              </div>
              <div>
                <Label>Contraseña inicial (mín 8 chars)</Label>
                <Input
                  type="password"
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  placeholder="Para que el owner pueda loguearse"
                  minLength={ownerPassword.length > 0 ? 8 : undefined}
                />
              </div>
              <div><Label>Industria</Label><Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="streetwear" /></div>
            </div>

            <div>
              <Label>Environment de la API key</Label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as 'live' | 'test')}
                className="w-full h-9 px-3 bg-[hsl(var(--surface))] border border-border rounded-md text-sm"
              >
                <option value="live">live</option>
                <option value="test">test</option>
              </select>
            </div>

            {provision.error && (
              <div className="p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                {(provision.error as Error).message}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={provision.isPending}>
                {provision.isPending ? <><Loader2 className="size-4 mr-2 animate-spin" /> Creando...</> : 'Crear tienda'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  )
}
